import { Form, Row } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useContext, useState } from 'react';
import { IAmlz } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import checkLeiApi from 'services/akcionar/emise/checkLei';
import isinRozcestnikApi, { IRozcestnikData } from 'services/akcionar/emise/rozcestnikISIN';
import { IcisStaty } from 'services/cisStaty';

import { ERozcestnikResponse } from 'enums/amlz/rozcestnikResponse';

import leiValidation from 'utils/validations/leiValidation';

interface IC0 {
    isLegalEntity: boolean;
    countries?: IcisStaty[];
    nextStep: () => void;
    setFormData: React.Dispatch<React.SetStateAction<IAmlz['amlz']>>;
    setRozcestnikData: React.Dispatch<React.SetStateAction<IRozcestnikData['spolecnost'] | undefined>>;
}

interface IFormValues {
    idmb: string;
    stat: string;
    lei: string;
}

const C0: React.FC<IC0> = ({ countries, nextStep, setRozcestnikData, isLegalEntity }) => {
    const { aktivniFirma } = useContext(Context);
    const [form] = Form.useForm();
    const [amlzCheck, setAmlzCheck] = useState<boolean>(false);
    const [dataToCheck, setDataToCheck] = useState<IRozcestnikData['spolecnost'] | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<IFormValues>();
    const [error, setError] = useState<boolean>(false);
    const [invalidLei, setInvalidLei] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<string>('');
    /*  const setForceRerender = useState<boolean>(false)[1];

   const rerender = () => {
        setForceRerender(val => !val);
    }; */

    const handleSelectChange = (name: string, value: string[] | string) => {
        if (typeof value === 'string') {
            form.setFieldsValue({
                [name]: value
            });
        } else {
            form.setFieldsValue({
                [name]: [...value]
            });
        }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        setLoading(true);
        setFormValues(fieldsValue);
        if (!amlzCheck || !fieldsValue.lei) {
            isinRozcestnikApi(fieldsValue)
                .then(data => {
                    // sluzba rozhoduje ci pustime usra dalej alebo prejde na check LEI
                    if (data.nasledujeweb === ERozcestnikResponse.AMLZLEI && !isLegalEntity) {
                        setDataToCheck(data.spolecnost);
                        setAmlzCheck(true);
                    } else {
                        setRozcestnikData(data.spolecnost);
                        nextStep();
                    }
                })
                .catch(() => setError(true))
                .finally(() => setLoading(false));
        } else {
            checkLeiApi(fieldsValue.lei)
                .then(data => {
                    if (
                        (fieldsValue.stat === 'CZ' && data.ic !== fieldsValue.idmb) ||
                        (fieldsValue.stat !== 'CZ' && fieldsValue.stat !== data.stat)
                    ) {
                        setInvalidLei(true);
                        setModalMessage('FORMULARE_LEI_VEDENO_PRO_JINOU_SPOL');
                    } else if (data.stav !== 'ISSUED' && data.stav !== 'PENDING_TRANSFER') {
                        setInvalidLei(true);
                        setModalMessage(data.stav === 'LAPSED' ? 'FORMULARE_LEI_LAPSED_FO' : 'FORMULARE_LEI_ZRUSENE');
                    } else {
                        setRozcestnikData({
                            idmb: data.ic,
                            stat: data.stat || aktivniFirma?.stat?.toUpperCase(),
                            lei: data.lei,
                            nazev: data.spolecnost,
                            ...dataToCheck
                        });
                        nextStep();
                    }
                })
                .catch(() => {
                    setInvalidLei(true);
                    setModalMessage('FORMULARE_AMLZ_ROZCESTNIK_INVALID_LEI');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    return (
        <>
            <Paragraph
                text={'FORMULARE_AMLZ_ROZCESTNIK_ZAKON'}
                style={{
                    width: '100%',
                    display: 'block',
                    paddingBottom: isLegalEntity ? 20 : 'initial'
                }}
            />
            {!isLegalEntity && (
                <>
                    {' '}
                    <Title level={5} text={'ZAKLADNI_UDAJE_O_EMITENTOVI'} />
                    <Paragraph
                        text={'FORMULARE_AMLZ_ROZCESTNIK_ZAKL_UDAJE1'}
                        style={{
                            width: '100%',
                            display: 'block',
                            paddingBottom: 20
                        }}
                    />
                </>
            )}

            <>
                <LoadingWrapper loading={!countries || loading}>
                    <FormWrapper name="C0" outForm={form} onFinish={onFinish}>
                        <InputComponent
                            disabled={true}
                            initialValue={aktivniFirma?.ic}
                            value={formValues?.idmb}
                            onChange={() => setError(false)}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmb"
                            type="text"
                            extramessage={error ? 'NEVALIDNI_ICO' : ''}
                        />
                        <SelectComponent
                            disabled={true}
                            defaultValue={aktivniFirma?.stat?.toUpperCase()}
                            isFormItem={true}
                            data={countries ? countries : []}
                            name="stat"
                            labelParagraph={'STAT_SIDLA'}
                            rules={[
                                {
                                    required: !amlzCheck,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                }
                            ]}
                            onChange={value => handleSelectChange('stat', String(value))}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={aktivniFirma?.ic}
                            value={formValues?.idmb}
                            onChange={() => setError(false)}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmb"
                            type="text"
                            extramessage={error ? 'NEVALIDNI_ICO' : ''}
                        />
                        {amlzCheck && (
                            <InputComponent
                                label="LEI_CELE"
                                name="lei"
                                type="text"
                                rules={leiValidation()}
                                notRequired={isLegalEntity}
                            />
                        )}
                        {/* isLegalEntity && (
                                <>
                                    <SelectComponent
                                        name="madic"
                                        labelParagraph="ISIN_IDENTIFIKACE_PLATCE"
                                        isFormItem={true}
                                        data={anoNe}
                                        onChange={() => {
                                            form.resetFields(['dic']);
                                            rerender();
                                        }}
                                    />
                                    {form.getFieldValue('madic') === EAnoNe.ANO && (
                                        <InputComponent
                                            //    initialValue={isin?.dic || identifikacePutData?.dic}
                                            label="ISIN_IDENTIFIKACE_DIC"
                                            name="dic"
                                            maxLength={12}
                                            rules={dicValidation()}
                                        />
                                    )}
                                </>
                            )*/}
                        <Row className="button-row">
                            {amlzCheck && (
                                <ButtonComponent
                                    id="C0ZpetBtn"
                                    type="default"
                                    onClick={() => setAmlzCheck(false)}
                                    buttontext="ZPET"
                                />
                            )}
                            <ButtonComponent
                                type="primary"
                                onClick={() => form.submit()}
                                id="C0PokracovatBtn"
                                buttontext="POKRACOVAT"
                            />
                        </Row>
                    </FormWrapper>
                </LoadingWrapper>
            </>
            <Modal
                title={<TranslateText>NEVALIDNI_LEI</TranslateText>}
                open={invalidLei}
                onCancel={() => setInvalidLei(false)}
                afterClose={() => {
                    setInvalidLei(false);
                    setLoading(false);
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="C0ZpetModalBtn"
                            buttontext="ZAVRIT"
                            type="default"
                            onClick={() => {
                                setInvalidLei(false);
                                setLoading(false);
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={modalMessage} />
                </Row>
            </Modal>
        </>
    );
};

export default C0;
